<script>
import Form from '@classes/Form'
import { medicalDateFormatter, isBeforeDate } from '@utils/medical-date'
import { authComputed, authMethods, optionsComputed } from '@state/helpers'
import { familyHistoryService } from '@services/FamilyHistoryService'
import { healthRecordService } from '@services/HealthRecordService'
import ComboBox from '@components/combo-box.vue'
import store from '@state/store'
import hrMixin from '@src/mixins/hr-mixin.vue'
import MedicalDatePickerDialog from '@components/dialogs/medical-date-picker-dialog.vue'
export default {
  page: {
    title: 'Family History Editor',
    meta: [{ name: 'description', content: 'The Family History Editor page.' }],
  },
  components: {
    ComboBox,
    MedicalDatePickerDialog,
  },
  mixins: [hrMixin],
  props: {
    formDialog: {
      type: Boolean,
      default: false,
    },
    formChanged: {
      type: Boolean,
      default: false,
    },
    formSaved: {
      type: Boolean,
      default: false,
    },
    formSavedAnother: {
      type: Boolean,
      default: false,
    },
    attachmentDeleted: {
      type: Boolean,
      default: false,
    },
    create: {
      type: Boolean,
      default: false,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      default: null,
    },
    id: {
      type: [Number, String],
      default: 0,
    },
    resource: {
      type: String,
      default: 'familyhistory',
    },

    show: {
      type: Boolean,
      default: false,
    },
    unauth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      familyHistoryForm: new Form({
        start: null,
        start_date_part: '',
        end: null,
        end_date_part: '',
        family_member_name: '',
        relationship_type: '',
        deceased: '',
        medical_problem: '',
        age_at_occurrence: '',
        outcome: '',
        note: '',
        private: false,
      }),
      loading: false,
      familyHistoryError: null,
      medicalDateStartPickerDialog: false,
      medicalDateEndPickerDialog: false,
      rules: {
        integerRule: (value) => {
          if (value) {
            if (typeof value === 'string') {
              if (!value.trim()) return true
            }
            if (!isNaN(parseInt(value)) && value >= 1 && value <= 120) {
              return true
            }
            return this.$t('health-record.family-history.integer-rule-message')
          }
          return true
        },
      },
      attachments: [],
      originalAttachments: [],
      showHRActions: true,
    }
  },
  computed: {
    ...authComputed,
    ...optionsComputed,
    relationships() {
      return this.getFamilyHistoryOptions?.relationship_type
        ? this.getFamilyHistoryOptions.relationship_type
        : []
    },
    outcomes() {
      return this.getFamilyHistoryOptions?.outcome
        ? this.getFamilyHistoryOptions.outcome
        : []
    },
    canSaveFamilyHistory() {
      if (
        this.familyHistoryForm.family_member_name &&
        this.familyHistoryForm.relationship_type &&
        this.familyHistoryForm.medical_problem &&
        this.familyHistoryForm.outcome &&
        this.familyHistoryError === null &&
        this.dateError === null
      ) {
        return true
      }
      return false
    },
    ageAtOccurrence: {
      get() {
        return this.familyHistoryForm.age_at_occurrence
          ? this.familyHistoryForm.age_at_occurrence
          : 0
      },
      set(value) {
        this.familyHistoryForm.age_at_occurrence = value
      },
    },
  },
  watch: {
    familyHistoryForm: {
      handler(newVal, oldVal) {
        if (newVal) {
          this.$emit('update:formChanged', true)
        }
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      this.loading = true

      if (this.edit) {
        if (this.item.patient_id !== null) {
          this.showHRActions = false
        }
        this.itemToForm(this.resource, this.item)
      }

      if (!this.getFamilyHistoryOptions) {
        const familyHistoryOptions = (await familyHistoryService.options()).data
        if (familyHistoryOptions) {
          store.dispatch(
            'options/setFamilyHistoryOptions',
            familyHistoryOptions
          )
        }
      }
    } catch (error) {
      console.warn(error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...authMethods,
    clearForm() {
      this.familyHistoryForm.reset()
      this.message = false
      this.attachments = []
      this.isAnother = false
      this.isContinue = false
      this.familyHistoryError = null
      this.dateError = null
    },
    setMedicalDate(medicalDate, past) {
      const field = this.medicalDatePickerName
      const fieldPart = this.medicalDatePickerName + '_date_part'

      const medicalDateObject = medicalDateFormatter(medicalDate)

      this.familyHistoryForm[field] = medicalDateObject.date
      this.familyHistoryForm[fieldPart] = this.determinePast(
        past,
        medicalDateObject
      )
      this.medicalDateStartPickerDialog = false
      this.medicalDateEndPickerDialog = false
    },
    async getFamilyHistoryResponse() {
      if (this.edit) {
        return (
          await healthRecordService.update(
            this.item.hr_id,
            this.familyHistoryForm.removeEmptyStringFromData()
          )
        ).data
      } else {
        return (
          await familyHistoryService.create(
            this.familyHistoryForm.removeEmptyFromData()
          )
        ).data
      }
    },
    async deleteAttachment(id) {
      try {
        const response = await familyHistoryService.attachmentDelete(id)
        if (response) {
          this.originalAttachments = this.originalAttachments.filter(function(
            obj
          ) {
            return obj.id !== id
          })
          this.$emit('update:attachmentDeleted', true)
        }
      } catch (error) {
        console.warn(error)
      }
    },
    setErrors(error) {
      this.familyHistoryForm.onFail(error.response.data)
      if (this.hasResponse) {
        this.message = true
        if (this.formDialog) {
          this.$emit('update:formSaved', true)
        }
      }
      this.familyHistoryError = error
    },
    async tryToSave(action) {
      this.resetFlagsAndErrors('familyHistoryError', action)
      try {
        if (
          this.familyHistoryForm.start &&
          isBeforeDate(
            this.familyHistoryForm.start,
            new Date().toISOString().split('T')[0]
          )
        ) {
          this.dateError = this.$t('start-after-today')
          this.scrollToErrors()
          return
        }

        if (
          this.familyHistoryForm.start &&
          this.familyHistoryForm.end &&
          isBeforeDate(this.familyHistoryForm.start, this.familyHistoryForm.end)
        ) {
          this.dateError = this.$t('end-before-start')
          this.scrollToErrors()
          return
        }
        const response = await this.getFamilyHistoryResponse()

        if (response) {
          if (document.getElementById('getAllData')) this.hasResponse = true
          let responseAttachments

          if (this.attachments.length > 0) {
            responseAttachments = (
              await familyHistoryService.attachments(
                this.attachments,
                response.hr_id,
                'familyhistory'
              )
            ).data

            console.warn(responseAttachments)
          }

          await this.fetchWizardData()

          this.message = true
          if (
            action === 'continue' &&
            store.state.calender.addNewRecord === 'true'
          ) {
            this.$emit('update:formSaved', true)
            this.$emit('close-dialog')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }

          if (action === 'continue') {
            this.$emit('update:formSaved', true)
            this.edit ? this.$emit('close-dialog') : this.$emit('back')
            if (document.getElementById('getAllData'))
              document.getElementById('getAllData').click()
          }
          if (action === 'another') {
            this.$emit('update:formSavedAnother', true)
            this.clearForm()
            this.$emit('add-another')
            if (document.getElementById('updateGetAllData'))
              document.getElementById('updateGetAllData').click()
          }
        }
      } catch (error) {
        console.warn(error)
        this.setErrors(error)
        this.isAnother = false
        this.isContinue = false
        this.scrollToErrors()
      } finally {
        this.tryingToSave = false
      }
    },
    itemToForm(res, item) {
      if (item.record.start) {
        this.familyHistoryForm.start = item.record.start
      }
      this.familyHistoryForm.start_date_part = item.record.start_date_part
      if (item.record.end) {
        this.familyHistoryForm.end = item.record.end
      }
      this.familyHistoryForm.end_date_part = item.record.end_date_part
      this.familyHistoryForm.family_member_name = item.record.family_member_name
      this.familyHistoryForm.relationship_type =
        item.record.relationship_type.key
      this.familyHistoryForm.deceased = item.record.deceased
      this.familyHistoryForm.medical_problem = item.record.medical_problem
      this.familyHistoryForm.age_at_occurrence = item.record.age_at_occurrence
      this.familyHistoryForm.outcome = item.record.outcome?.key
      this.familyHistoryForm.note = item.note
      this.familyHistoryForm.private = item.private
      this.originalAttachments = item.attachments.map((element) => {
        return { ...element }
      })
    },
  },
}
</script>

<template>
  <v-container @dragover.prevent @drop="onDrop">
    <v-row
      no-gutters
      :class="$style.hrHeader"
      class="primary--text mt-1"
      justify="center"
    >
      <div class="mr-4">
        <v-img
          :src="
            require('@assets/images/safehaven/health-record-icons/family-history.svg')
          "
          height="30"
          width="30"
          contain
        ></v-img>
      </div>
      {{ $t('health-record.family-history.form_title') }}
    </v-row>

    <v-row
      :class="$style.hrQuestionFull"
      class="mt-10 offblack--text"
      no-gutters
      justify="center"
    >
      What's the family member's name?
    </v-row>

    <v-row class="mt-10" no-gutters justify="center">
      <v-text-field
        v-model="familyHistoryForm.family_member_name"
        placeholder="Name"
        :class="$style.hrTextField"
        rounded
        filled
        background-color="input"
        :disabled="show"
      />
    </v-row>

    <div v-if="familyHistoryForm.family_member_name || edit">
      <v-row
        :class="$style.hrQuestionFull"
        class="mt-10 offblack--text"
        no-gutters
        justify="center"
      >
        What's your relation to {{ familyHistoryForm.family_member_name }}?
      </v-row>
      <v-row class="mt-10" no-gutters justify="center">
        <v-autocomplete
          v-model="familyHistoryForm.relationship_type"
          :disabled="show"
          class="familyHistoryRelationshipType"
          attach=".familyHistoryRelationshipType"
          :items="relationships"
          item-text="value"
          item-value="key"
          placeholder="Select one"
          rounded
          filled
          background-color="input"
          clearable
          :class="$style.hrAutoComplete"
          :error-messages="
            familyHistoryForm.errors.has('relationship_type')
              ? 'Relationship Type: ' +
                familyHistoryForm.errors.get('relationship_type')
              : ''
          "
          @change="
            clearFormFromErrors('relationship_type', 'familyHistoryForm')
          "
        ></v-autocomplete>
      </v-row>

      <v-row class="mt-7">
        <v-col cols="12" md="6">
          <v-row
            :class="$style.hrQuestion"
            class="my-auto offblack--text"
            justify="center"
            no-gutters
          >
            Is this family member deceased?
          </v-row>
        </v-col>
        <v-col cols="12" md="6">
          <v-radio-group
            v-model="familyHistoryForm.deceased"
            :disabled="show"
            row
            class="my-auto"
            :class="$style.hrRadio"
          >
            <v-row class="my-auto" justify-md="start" justify="center">
              <v-radio label="No" :value="false"></v-radio>
              <v-radio label="Yes" :value="true"></v-radio>
            </v-row>
          </v-radio-group>
        </v-col>
      </v-row>

      <v-row
        :class="$style.hrQuestionFull"
        class="mt-7 offblack--text"
        no-gutters
        justify="center"
      >
        {{
          familyHistoryForm.deceased
            ? `What was your family member's medical condition?`
            : `What's your family member's medical condition?`
        }}
      </v-row>

      <v-row class="mt-10" no-gutters justify="center">
        <div :class="$style.hrComboBoxContainer">
          <ComboBox
            :key="'family-history-combo-1'"
            :disabled="show"
            :combo-class="'family-history-combo-1'"
            :value.sync="familyHistoryForm.medical_problem"
            placeholder="e.g. Chronic fatigue syndrome"
            :fetch-url="unauth ? null : '/api/v1/hr/choices/condition/'"
            rounded
            filled
            background-color="input"
            :class="$style.hrComboBox"
            :error-messages="
              familyHistoryForm.errors.has('medical_problem')
                ? 'Medical Problem: ' +
                  familyHistoryForm.errors.get('medical_problem')
                : ''
            "
            @keydown="
              clearFormFromErrors(
                'medical_problem',
                'familyHistoryForm',
                // eslint-disable-next-line vue/comma-dangle
                'familyHistoryError'
              )
            "
          ></ComboBox>
        </div>
      </v-row>

      <v-row class="mt-10">
        <div :class="$style.hrQuestionFull" class="offblack--text"
          >How long has {{ familyHistoryForm.family_member_name }} been
          experiencing this condition?
        </div>
      </v-row>
      <v-row v-if="dateError" class="ml-1">
        <v-col cols="12" md="12">
          <p class="error--text">{{ dateError }}</p>
        </v-col>
      </v-row>
      <v-row class="mt-10">
        <v-col cols="12" md="6">
          <div
            :class="
              mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
            "
          >
            <MedicalDatePickerDialog
              :key="'familyHistoryDate-1'"
              :disabled="show"
              :dialog.sync="medicalDateStartPickerDialog"
              :title="'health-record.family-history.start-date'"
              :class="$style.hrMedicalDatePicker"
              :field="familyHistoryForm.start"
              :part="familyHistoryForm.start_date_part"
              filled
              background-color="input"
              :error-messages="
                familyHistoryForm.errors.has('start')
                  ? 'Start Date: ' + familyHistoryForm.errors.get('start')
                  : ''
              "
              @input="
                clearErrors(
                  'start',
                  'familyHistoryForm',
                  // eslint-disable-next-line vue/comma-dangle
                  'familyHistoryError'
                )
              "
              @setMedicalDate="setMedicalDate"
              @showMedicalDate="showMedicalDate('start')"
            />
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <div
            :class="
              mobile ? $style.medicalDateDivMobile : $style.medicalDateDiv
            "
          >
            <MedicalDatePickerDialog
              :key="'familyHistoryDate-2'"
              :disabled="show"
              :dialog.sync="medicalDateEndPickerDialog"
              :title="'health-record.family-history.end-date'"
              :field="familyHistoryForm.end"
              :part="familyHistoryForm.end_date_part"
              filled
              background-color="input"
              @setMedicalDate="setMedicalDate"
              @showMedicalDate="showMedicalDate('end')"
              @input="dateError = null"
            />
          </div>
        </v-col>
      </v-row>

      <v-row
        :class="$style.hrQuestionFull"
        class="mt-7 offblack--text"
        no-gutters
        justify="center"
      >
        Was there any specific outcome related to the condition?
      </v-row>
      <v-row class="mt-10" no-gutters justify="center">
        <v-autocomplete
          v-model="familyHistoryForm.outcome"
          :disabled="show"
          class="familyHistoryOutcome"
          attach=".familyHistoryOutcome"
          :items="outcomes"
          item-text="value"
          item-value="key"
          placeholder="Select one"
          rounded
          filled
          background-color="input"
          clearable
          :class="$style.hrAutoComplete"
          :error-messages="
            familyHistoryForm.errors.has('outcome')
              ? 'Outcome: ' + familyHistoryForm.errors.get('outcome')
              : ''
          "
          @change="clearFormFromErrors('outcome', 'familyHistoryForm')"
        ></v-autocomplete>
      </v-row>

      <v-row class="mt-7">
        <div :class="$style.hrQuestionFull" class="offblack--text"
          >Any additional files or notes you'd like to include?
        </div>
      </v-row>
      <v-row class="mt-10">
        <div class="mx-auto" :class="$style.hrInputDiv">
          <SpeechToText
            :key="'family-history-speech-2'"
            :disabled="show"
            :value.sync="familyHistoryForm.note"
            auto-grow
            rounded
            :class="$style.hrSpeechToText"
            :placeholder="$t('health-record.family-history.note')"
          ></SpeechToText>
        </div>
      </v-row>
      <v-row class="mt-10">
        <div class="mx-auto" :class="$style.hrInputDiv">
          <FileUpload
            :key="'family-history-file-3'"
            :disabled="show"
            icon
            :height="200"
            :multiple="true"
            :files.sync="attachments"
            :non-draggable="true"
          ></FileUpload>
        </div>
      </v-row>

      <v-row v-if="edit" class="mt-10">
        <div class="mx-auto" :class="$style.hrInputDiv">
          <AttachmentList
            :attachments="originalAttachments"
            @delete-attachment="deleteAttachment"
          ></AttachmentList>
        </div>
      </v-row>

      <ForMyEyesOnly v-if="!show" :value.sync="familyHistoryForm.private" />
      <div v-if="showHRActions">
        <HrActions
          :can-save="canSaveFamilyHistory"
          :is-another="isAnother"
          :is-continue="isContinue"
          :trying-to-save="tryingToSave"
          :trying-to-delete="tryingToDelete"
          :edit="edit"
          @go-back="goBack"
          @try-to-save="tryToSave"
          @try-to-delete="prepareDeletion"
        />
      </div>
    </div>

    <v-row v-if="familyHistoryError" class="ml-1">
      <v-col cols="12" md="12">
        <p class="error--text">{{
          $t('health-record.family-history.error')
        }}</p>
        <div v-if="familyHistoryForm.errors.has('non_field_errors')">
          <div
            v-for="(error, index) in familyHistoryForm.errors.getNonField(
              // eslint-disable-next-line vue/comma-dangle
              'non_field_errors'
            )"
            :key="'error-non-field' + index"
          >
            <p class="error--text">{{ error }}</p>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="message" class="ml-1">
      <p class="success--text">{{
        $t('health-record.family-history.message')
      }}</p>
    </v-row>
    <BaseConfirmDialog
      v-if="confirmDialog"
      :dialog="confirmDialog"
      :message="computedMessage"
      title="Delete Item"
      @cancel="cancel"
      @confirm="deleteItem"
    />
  </v-container>
</template>

<style lang="scss" module>
@import '@design';
.hrHeader {
  // styleName: Desktop/Display X-Small Bold;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestion {
  // styleName: Desktop/Display X-Small;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrQuestionFull {
  // styleName: Desktop/Display X-Small;
  width: 100%;
  font-family: $system-default-font-family;
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: center;
  letter-spacing: 1px;
}
.hrRadio {
  // styleName: Mobile / Display Small;
  font-family: $system-default-font-family;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  text-align: left;
  letter-spacing: 1px;
}
.hrComboBoxContainer {
  width: 394px;
}
.hrComboBox {
  border-radius: 15px !important;
}
.hrTextField {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrAutoComplete {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrSelect {
  max-width: 394px !important;
  border-radius: 15px !important;
}
.hrInputDiv {
  width: 394px;
  border-radius: 15px !important;
}
.hrSpeechToText {
  max-width: 394px !important;
  background: $color-theme-light-input;
}
.hrButton {
  border-radius: 8px !important;
}
.hrTextFieldSmall {
  max-width: 90px !important;
  font-size: 25px !important;
  border-radius: 15px !important;
}
.hrTextFieldSmall input {
  text-align: center !important;
}
.medicalDateDiv {
  width: 394px;
  margin-right: auto;
  margin-left: auto;
}
.medicalDateDivMobile {
  width: 60%;
  margin-right: auto;
  margin-left: auto;
}
</style>
